import { supabase } from '@/lib/supabase';
import { CacheData, Categoria, FAQ, Prestador, Profile, Servico, Subcategoria } from '@/types/common';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useSupabaseOptimized } from './useSupabaseOptimized';

const CACHE_KEY = 'tamoaki_offline_cache';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 horas,
  export const useOfflineCache = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [cacheData, setCacheData] = useState<CacheData>({});
  const [isSyncing, setIsSyncing] = useState(false);
  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSyncAttemptRef = useRef<number>(0);
  const hasInitialSyncRef = useRef<boolean>(false);
  const { makeRequest } = useSupabaseOptimized();

  // Carregar cache do localStorage,
  const loadCache = useCallback((): CacheData => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const data = JSON.parse(cached) as CacheData;
        return data;
      }
    } catch (error) {
      console.error('Erro ao carregar cache:', error);
    }
    return {};
  }, []);

  // Salvar cache no localStorage,
  const saveCache = useCallback((data: CacheData) => {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Erro ao salvar cache:', error);
    }
  }, []);

  // Verificar se o cache é válido,
  const isCacheValid = useCallback((data: CacheData): boolean => {
    if (!data.lastSync) return false;
    const now = Date.now();
    return (now - data.lastSync) < CACHE_DURATION;
  }, []);

  // Sincronizar dados offline,
  const syncOfflineData = useCallback(async () => {
    if (isSyncing) {
      if (import.meta.env.DEV) console.log('Sincronização já em andamento...');
      return;
    }

    const now = Date.now();
    if (now - lastSyncAttemptRef.current < 5000) {
      if (import.meta.env.DEV) console.log('Tentativa muito recente, aguardando...');
      return;
    }

    lastSyncAttemptRef.current = now;
    setIsSyncing(true);

    const SYNC_TIMEOUT_MS = 10_000;

    const doSync = async () => {
      if (import.meta.env.DEV) console.log('Iniciando sincronização offline...');

      // Buscar dados do usuário,
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const profileResult = await makeRequest(
          'profile',
          () => supabase.from('profiles').select('*').eq('id', user.id).single(),
          { cache: true }
        );
        if (profileResult.data) {
          setCacheData(prev => ({ ...prev, profile: profileResult.data as Profile }));
        }
      }

      // Buscar categorias,
      const categoriasResult = await makeRequest(
        'categorias',
        () => supabase.from('categorias').select('*').order('ordem', { ascending: true }),
        { cache: true }
      );
      if (categoriasResult.data) {
        setCacheData(prev => ({ ...prev, categorias: categoriasResult.data as Categoria[] }));
      }

      // Buscar subcategorias,
      const subcategoriasResult = await makeRequest(
        'subcategorias',
        () => supabase.from('subcategorias').select('*').order('ordem', { ascending: true }),
        { cache: true }
      );
      if (subcategoriasResult.data) {
        setCacheData(prev => ({ ...prev, subcategorias: subcategoriasResult.data as Subcategoria[] }));
      }

      // Buscar prestadores,
      const prestadoresResult = await makeRequest(
        'prestadores',
        () => supabase
          .from('prestadores')
          .select(`
            id,
            descricao,
            nota_media,
            total_avaliacoes,
            profiles!inner(full_name, avatar_url, verificado)
          `)
          .order('nota_media', { ascending: false }),
        { cache: true }
      );
      if (prestadoresResult.data) {
        setCacheData(prev => ({ ...prev, prestadores: prestadoresResult.data as unknown as Prestador[] }));
      }

      // Buscar serviços ativos,
      const servicosResult = await makeRequest(
        'servicos',
        () => supabase.from('servicos').select('*').eq('ativo', true),
        { cache: true }
      );
      if (servicosResult.data) {
        setCacheData(prev => ({ ...prev, servicos: servicosResult.data as Servico[] }));
      }

      // Buscar FAQs,
      const faqsResult = await makeRequest(
        'faqs',
        () => supabase
          .from('faqs')
          .select(`
            *,
            categoria:faq_categorias(*)
          `)
          .eq('visivel', true)
          .order('ordem', { ascending: true }),
        { cache: true }
      );
      if (faqsResult.data) {
        setCacheData(prev => ({ ...prev, faqs: faqsResult.data as FAQ[] }));
      }

      // Atualizar timestamp de sincronização,
      const updatedCache = {
        ...cacheData,
        lastSync: Date.now()
      };
      setCacheData(updatedCache);
      saveCache(updatedCache);

      if (import.meta.env.DEV) console.log('Sincronização offline concluída');

      // Exibir toast de sucesso com cooldown para evitar repetição excessiva
      try {
        const LAST_SYNC_TOAST_KEY = 'tamoaki_last_sync_toast_at';
        const lastShownAt = Number(localStorage.getItem(LAST_SYNC_TOAST_KEY) || 0);
        const nowTs = Date.now();
        const COOLDOWN_MS = 10 * 60 * 1000; // 10 minutos
        if (!lastShownAt || nowTs - lastShownAt > COOLDOWN_MS) {
          toast.success('Dados sincronizados com sucesso!');
          localStorage.setItem(LAST_SYNC_TOAST_KEY, String(nowTs));
        }
      } catch {
        // Falha silenciosa ao acessar localStorage não deve impactar UX
      }
    };

    try {
      await Promise.race([
        doSync(),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('sync_timeout')), SYNC_TIMEOUT_MS)
        ),
      ]);
    } catch (error) {
      if ((error as Error)?.message !== 'sync_timeout') {
        console.error('Erro na sincronização offline:', error);
      }
    } finally {
      setIsSyncing(false);
    }
  }, [isSyncing, makeRequest, cacheData, saveCache]);

  // Monitorar status online/offline,
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Toast de conexão restaurada pode ser ruidoso; mostrar com cooldown curto
      try {
        const KEY = 'tamoaki_last_online_toast_at';
        const lastAt = Number(localStorage.getItem(KEY) || 0);
        const nowTs = Date.now();
        const COOLDOWN_MS = 60 * 1000; // 60s
        if (!lastAt || nowTs - lastAt > COOLDOWN_MS) {
          toast.success('Conexão restaurada!');
          localStorage.setItem(KEY, String(nowTs));
        }
      } catch {
        if (import.meta.env.DEV) console.debug('online toast cooldown storage unavailable');
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      // Toast de offline com cooldown curto
      try {
        const KEY = 'tamoaki_last_offline_toast_at';
        const lastAt = Number(localStorage.getItem(KEY) || 0);
        const nowTs = Date.now();
        const COOLDOWN_MS = 60 * 1000; // 60s
        if (!lastAt || nowTs - lastAt > COOLDOWN_MS) {
          toast.warning('Modo offline ativado');
          localStorage.setItem(KEY, String(nowTs));
        }
      } catch {
        if (import.meta.env.DEV) console.debug('offline toast cooldown storage unavailable');
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Carregar cache inicial,
  useEffect(() => {
    const cached = loadCache();
    setCacheData(cached);
  }, [loadCache]);

  // Cleanup do timeout,
  useEffect(() => {
    const timeoutId = syncTimeoutRef.current;
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  const hasOfflineData = Object.keys(cacheData).length > 0 && cacheData.lastSync;

  return {
    isOnline,
    cacheData,
    isSyncing,
    hasOfflineData,
    isCacheValid: isCacheValid(cacheData),
    syncOfflineData
  };
};